import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from '../components/ui/Logo';
import { useAuth } from '../context/AuthContext';

/**
 * Register Page - Passwordless Authentication
 * 
 * With the new passwordless system, registration and login are combined.
 * This page provides information and redirects to the login page where
 * new users will automatically be registered when they enter their email
 * and name for the first time.
 */
const Register = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleGetStarted = () => {
    navigate('/login', { state: { isRegistering: true } });
  };

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=1280&q=60&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.1) brightness(0.7)'
        }}
      />

      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Header */}
      <header className="relative z-50 flex items-center justify-between px-3 sm:px-6 py-4 sm:py-6 md:px-12 max-w-7xl mx-auto border-b border-premium-gold/20">
        <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <Logo variant="sidebar" size="sm" />
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <p className="hidden sm:block text-sm text-gray-400">Already have an account?</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 text-sm"
          >
            Log In
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-8 flex items-center justify-center min-h-[85vh]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-4xl"
        >
          {/* Info Card */}
          <div className="relative p-6 border border-premium-gold/30 rounded-xl bg-gradient-to-br from-white/5 to-transparent backdrop-blur-xl overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-premium-gold/5 to-transparent opacity-50"></div>

            <div className="relative z-10 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring' }}
                className="inline-flex items-center justify-center w-14 h-14 bg-premium-gold/20 rounded-full mb-3"
              >
                <svg className="w-7 h-7 text-premium-gold-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </motion.div>

              <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-premium-gold-light via-white to-premium-gold-light bg-clip-text text-transparent">
                Passwordless Sign Up
              </h1>

              <p className="text-base text-gray-300 mb-5">
                Create your account in seconds with just your email
              </p>

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 text-left">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="p-3 bg-white/5 border border-premium-gold/20 rounded-lg"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-premium-gold-light flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <h3 className="font-semibold text-white text-sm">No Passwords</h3>
                  </div>
                  <p className="text-xs text-gray-400">
                    Nothing to remember
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="p-3 bg-white/5 border border-premium-gold/20 rounded-lg"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-premium-gold-light flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <h3 className="font-semibold text-white text-sm">More Secure</h3>
                  </div>
                  <p className="text-xs text-gray-400">
                    Email codes only
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="p-3 bg-white/5 border border-premium-gold/20 rounded-lg"
                >
                  <div className=" flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-premium-gold-light flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                    <h3 className="font-semibold text-white text-sm">Lightning Fast</h3>
                  </div>
                  <p className="text-xs text-gray-400">
                    Sign up fast
                  </p>
                </motion.div>
              </div>

              {/* How it Works */}
              <div className="mb-5 p-4 bg-premium-gold/10 border border-premium-gold/30 rounded-lg text-left">
                <h3 className="text-sm font-semibold text-premium-gold-light mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                  How It Works
                </h3>
                <ol className="space-y-2 text-gray-300 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-premium-gold/20 rounded-full flex items-center justify-center text-premium-gold-light text-xs font-bold">1</span>
                    <span>Enter email and name</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-premium-gold/20 rounded-full flex items-center justify-center text-premium-gold-light text-xs font-bold">2</span>
                    <span>Get 6-digit code</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-premium-gold/20 rounded-full flex items-center justify-center text-premium-gold-light text-xs font-bold">3</span>
                    <span>You're in! 🎉</span>
                  </li>
                </ol>
              </div>

              {/* CTA Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetStarted}
                className="w-full py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all mb-3"
              >
                Get Started - It's Free
              </motion.button>

              <p className="text-xs text-gray-500">
                By signing up, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Register;